import { getAuthHeaders } from "./authHelper";
import type {
    FoodMenuItem,
    FoodOrder,
    FoodOrderStatus,
    FoodPersonSearchResult,
    FoodWasteLog,
    CreateFoodWasteLogPayload,
    CreateFoodOrderResult
} from "../Pages/Admin/Food/foodTypes";



const apiUrl = process.env.REACT_APP_API_URL as string;

async function ensureOk(res: Response) {
    if (!res.ok) {
        const text = await res.text();

        const error: any = new Error(
            text || `HTTP ${res.status}`
        );

        error.status = res.status;
        error.data = text
            ? (() => {
                try {
                    return JSON.parse(text);
                } catch {
                    return null;
                }
            })()
            : null;

        throw error;
    }

    return res;
}

export async function getFoodMenu(
    eventId: number
): Promise<FoodMenuItem[]> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/menu`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function createFoodOrder(
    eventId: number,
    payload: {
        cardCode: string;
        personId?: number | null;
        personName?: string;
        note?: string;
        scheduledFor?: string | null;

        // NY
        splitOrder?: boolean;

        // Allow order even when person is outside Player Lounge
        allowOutsideZone?: boolean;

        items: {
            menuItemId: number;
            quantity: number;
        }[];
    }
): Promise<CreateFoodOrderResult> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/orders`,
        {
            method: "POST",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(payload),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function getKitchenOrders(
    eventId: number
): Promise<FoodOrder[]> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/orders`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    return res.json();
}

function foodOrderStatusToNumber(
    status: FoodOrderStatus
): number {
    switch (status) {
        case "Received":
            return 0;

        case "InProgress":
            return 1;

        case "Ready":
            return 2;

        case "Delivered":
            return 3;

        case "Cancelled":
            return 4;
    }
}

export async function setFoodOrderStatus(
    eventId: number,
    orderId: number,
    status: FoodOrderStatus
): Promise<FoodOrder> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/orders/${orderId}/status`,
        {
            method: "PATCH",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                status: foodOrderStatusToNumber(status),
            }),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function setFoodOrderSchedule(
    eventId: number,
    orderId: number,
    scheduledFor: string | null
): Promise<FoodOrder> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/orders/${orderId}/schedule`,
        {
            method: "PATCH",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                scheduledFor,
            }),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function getMyFoodOrders(
    eventId: number,
    userId: number
): Promise<FoodOrder[]> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/users/${userId}/orders`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    return res.json();
}

type PersonApiItem = {
    personId?: number;
    PersonId?: number;
    firstName?: string | null;
    FirstName?: string | null;
    lastName?: string | null;
    LastName?: string | null;
};

type PersonsPagedResponse = {
    items?: PersonApiItem[];
    Items?: PersonApiItem[];
};

export async function searchPersonsForFood(
    eventId: number,
    search: string
): Promise<FoodPersonSearchResult[]> {
    const res = await fetch(
        `${apiUrl}/api/persons/food-search?eventId=${eventId}&search=${encodeURIComponent(
            search.trim()
        )}`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    const data = await res.json();

    // food-search returnerer direkte et array
    if (!Array.isArray(data)) {
        return [];
    }

    return data.map(person => ({
        personId: person.personId,
        firstName: person.firstName ?? "",
        lastName: person.lastName ?? "",
        displayName:
            person.displayName ??
            `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim(),
    }));
}

export interface CreateFoodMenuItemPayload {
    name: string;
    category: string;
    price: number;
    imageUrl?: string;
    sortOrder: number;
}

export interface UpdateFoodMenuItemPayload {
    name?: string;
    category?: string;
    price?: number;
    imageUrl?: string;
    sortOrder?: number;
    isActive?: boolean;
}

export async function getAdminFoodMenu(
    eventId: number
): Promise<FoodMenuItem[]> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/menu/admin`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function createFoodMenuItem(
    eventId: number,
    payload: CreateFoodMenuItemPayload
): Promise<FoodMenuItem> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/menu`,
        {
            method: "POST",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(payload),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function updateFoodMenuItem(
    eventId: number,
    itemId: number,
    payload: UpdateFoodMenuItemPayload
): Promise<FoodMenuItem> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/menu/${itemId}`,
        {
            method: "PATCH",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(payload),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function setFoodMenuItemActive(
    eventId: number,
    itemId: number,
    active: boolean
): Promise<void> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/menu/${itemId}/active?active=${active}`,
        {
            method: "PATCH",
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);
}

export async function deleteFoodMenuItem(
    eventId: number,
    itemId: number
): Promise<void> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/menu/${itemId}`,
        {
            method: "DELETE",
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);
}
export async function setFoodOrderUnitStatus(
    eventId: number,
    orderId: number,
    unitId: number,
    status: FoodOrderStatus
): Promise<FoodOrder> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/orders/${orderId}/units/${unitId}/status`,
        {
            method: "PATCH",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify({
                status:
                    foodOrderStatusToNumber(status),
            }),
        }
    );

    await ensureOk(res);

    return res.json();
}



// Tilf�j disse funktioner til foodApi.ts:

export async function createFoodWasteLog(
    eventId: number,
    payload: CreateFoodWasteLogPayload
): Promise<FoodWasteLog> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/waste`,
        {
            method: "POST",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(payload),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function getFoodWasteLogs(
    eventId: number
): Promise<FoodWasteLog[]> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/waste`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function deleteFoodWasteLog(
    eventId: number,
    wasteId: number
): Promise<void> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/waste/${wasteId}`,
        {
            method: "DELETE",
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);
}

export async function getFoodEstimateSettings(
    eventId: number
): Promise<{
    microwaveCount: number;
    minutesPerBox: number;
}> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/settings`,
        {
            headers: getAuthHeaders(),
        }
    );

    await ensureOk(res);

    return res.json();
}

export async function updateFoodEstimateSettings(
    eventId: number,
    settings: {
        microwaveCount: number;
        minutesPerBox: number;
    }
): Promise<{
    microwaveCount: number;
    minutesPerBox: number;
}> {
    const res = await fetch(
        `${apiUrl}/api/events/${eventId}/food/settings`,
        {
            method: "PUT",
            headers: getAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(settings),
        }
    );

    await ensureOk(res);

    return res.json();
}